import React, { ReactNode } from "react";
import clsx from "clsx";

// Heading Component
interface HeadingProps {
  level: 1 | 2 | 3 | 4 | 5 | 6 | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  children: ReactNode;
  className?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl";
  id?: string;
}

export const Heading: React.FC<HeadingProps> = ({ level, children, className, size, id }) => {
  const baseStyles = "tracking-tight";

  // Normalize level: convert string "h1" to number 1, etc.
  const normalizedLevel: 1 | 2 | 3 | 4 | 5 | 6 =
    typeof level === "string"
      ? (parseInt(level.replace("h", ""), 10) as 1 | 2 | 3 | 4 | 5 | 6)
      : level;

  const levelStyles = {
    1: "text-4xl font-bold text-gray-100",
    2: "text-3xl font-semibold text-gray-100",
    3: "text-2xl font-semibold text-gray-200",
    4: "text-xl font-medium text-gray-200",
    5: "text-lg font-medium text-gray-300",
    6: "text-base font-medium text-gray-400",
  };

  const sizeStyles = {
    xs: "text-xs",
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg",
    xl: "text-xl",
    "2xl": "text-2xl",
    "3xl": "text-3xl",
    "4xl": "text-4xl",
  };

  const Tag = `h${normalizedLevel}` as keyof React.JSX.IntrinsicElements;

  return React.createElement(
    Tag as React.ElementType,
    {
      className: clsx(
        baseStyles,
        size ? sizeStyles[size] : levelStyles[normalizedLevel],
        className,
      ),
      ...(id ? { id } : {}),
    },
    children,
  );
};

// Text Component
interface TextProps {
  size?: "sm" | "md" | "lg";
  muted?: boolean;
  children: ReactNode;
  className?: string;
}

export const Text: React.FC<TextProps> = ({ size = "md", muted = false, children, className }) => {
  const sizeStyles = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg",
  };

  const colorStyles = muted ? "text-gray-500" : "text-gray-200";

  return <p className={clsx(sizeStyles[size], colorStyles, className)}>{children}</p>;
};

// Label Component
interface LabelProps {
  children: ReactNode;
  className?: string;
  muted?: boolean;
  htmlFor?: string;
}

export const Label: React.FC<LabelProps> = ({ children, className, muted = false, htmlFor }) => {
  const colorStyles = muted ? "text-gray-500" : "text-gray-300";

  if (htmlFor) {
    return (
      <label htmlFor={htmlFor} className={clsx("text-sm font-medium", colorStyles, className)}>
        {children}
      </label>
    );
  }

  return <span className={clsx("text-sm font-medium", colorStyles, className)}>{children}</span>;
};

// Form Label Component
interface FormLabelProps {
  children: ReactNode;
  className?: string;
}

export const FormLabel: React.FC<FormLabelProps> = ({ children, className }) => {
  return (
    <label className={clsx("text-sm font-semibold text-tracq-bright-blue", className)}>
      {children}
    </label>
  );
};

// Muted Component
interface MutedProps {
  children: ReactNode;
  className?: string;
}

export const Muted: React.FC<MutedProps> = ({ children, className }) => {
  return <span className={clsx("text-sm text-gray-500", className)}>{children}</span>;
};

// Code Component
interface CodeProps {
  children: ReactNode;
  className?: string;
  block?: boolean;
}

export const Code: React.FC<CodeProps> = ({ children, className, block = false }) => {
  const baseStyles = "font-mono text-sm bg-gray-800 px-1.5 py-0.5 rounded";
  const blockStyles = block ? "block p-4 my-4" : "inline";

  const Tag = block ? "pre" : "code";

  return React.createElement(
    Tag,
    {
      className: clsx(baseStyles, blockStyles, className),
    },
    children,
  );
};

// Blockquote Component
interface BlockquoteProps {
  children: ReactNode;
  className?: string;
}

export const Blockquote: React.FC<BlockquoteProps> = ({ children, className }) => {
  return (
    <blockquote
      className={clsx("border-l-4 border-tracq-deep-blue pl-4 italic text-gray-300", className)}
    >
      {children}
    </blockquote>
  );
};

// Link Component
interface LinkProps {
  children: ReactNode;
  className?: string;
  variant?: "default" | "muted";
  href?: string;
  onClick?: () => void;
}

export const Link: React.FC<LinkProps> = ({
  children,
  className,
  variant = "default",
  href,
  onClick,
}) => {
  const variantStyles = {
    default: "text-tracq-bright-blue hover:underline",
    muted: "text-gray-400 hover:text-tracq-bright-blue",
  };

  const baseStyles = "transition-colors duration-200";

  if (href) {
    return (
      <a href={href} className={clsx(baseStyles, variantStyles[variant], className)}>
        {children}
      </a>
    );
  }

  return (
    <button onClick={onClick} className={clsx(baseStyles, variantStyles[variant], className)}>
      {children}
    </button>
  );
};
